import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DoctorCard from './DoctorCard';
import Swal from 'sweetalert2';
import { alertaGeneral, alertaToastTopEnd } from '../../utils/alertas';
import { useDataUser } from '../../context/dataUserContext';
import { useMembership } from '../../context/MembershipContext';
import apiClient from '../../utils/axiosPetition';
import doctorService, { ESPECIALIDADES_DIRECTORIO } from '../../api/doctorService';
import SvgIcon from '../../utils/Icon';
import { ReactComponent as Directorio } from '../../assets/icons/contacto.svg';
import { ReactComponent as Buscar } from '../../assets/icons/lupa.svg';
import Cargando from '../../utils/Cargando';
import { FaCrown } from 'react-icons/fa';

const DoctorsGrid = ({ doctors, gridClassName = 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-6' }) => (
    <div className={gridClassName}>
        {doctors.map(doc => (
            <div key={doc._id} className="flex justify-center sm:block transition-transform duration-200 hover:-translate-y-0.5">
                <DoctorCard doctor={doc} />
            </div>
        ))}
    </div>
);

const Marketplace = () => {
    const [servicio, setServicio] = useState('');
    const [doctors, setDoctors] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [totalDoctors, setTotalDoctors] = useState(0);
    const [premiumDoctors, setPremiumDoctors] = useState(0);
    const [doctorHasHorarios, setDoctorHasHorarios] = useState(null);
    const [checkingHorarios, setCheckingHorarios] = useState(false);

    const { isAuth, role, doctorId, clinicId } = useDataUser();
    const navigate = useNavigate();

    const loadPremiumDoctors = async () => {
        try {
            setCargando(true);
            // Pasar clinicId del contexto por si no está en localStorage (paciente recién registrado / OAuth)
            const result = await doctorService.getPremiumDoctors(isAuth, clinicId);
            
            if (result.success) {
                setDoctors(result.doctors);
                setTotalDoctors(result.total);
                setPremiumDoctors(result.premium);
            } else {
                setDoctors([]);
                setTotalDoctors(0);
                setPremiumDoctors(0);
            }
        } catch (error) {
            console.error('❌ Error cargando doctores PREMIUM:', error);
            alertaToastTopEnd({
                mensaje: error?.response?.data?.message || 'Ocurrió un error al cargar los profesionales PREMIUM',
                isError: true
            });
            setDoctors([]);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        localStorage.removeItem('selectedDate');
        if (role === 'doctor' && doctorId) {
            setCheckingHorarios(true);
            // Solo doctores asistenciales deben tener horarios para usar el directorio.
            // Expertos (sin prestación clínica) también pueden contratar a otros como pacientes.
            apiClient.get(`/api/afiliado/getState/${doctorId}`)
                .then((stateRes) => {
                    const tipoPerfil = stateRes.data?.tipoPerfil;
                    const prestaServicios = stateRes.data?.prestacionServicio;
                    const esExperto = tipoPerfil === 'experto' || prestaServicios === false;
                    if (esExperto) {
                        setDoctorHasHorarios(true);
                        return loadPremiumDoctors();
                    }
                    return apiClient.get(`/api/doctor/horarios/${doctorId}`).then((res) => {
                        const tieneHorarios = res.data.horarios && res.data.horarios.length > 0;
                        setDoctorHasHorarios(tieneHorarios);
                        if (tieneHorarios) {
                            return loadPremiumDoctors();
                        }
                    });
                })
                .catch(() => {
                    // No bloquear el directorio si falla la verificación (el doctor también usa el rol paciente)
                    setDoctorHasHorarios(true);
                    loadPremiumDoctors();
                })
                .finally(() => setCheckingHorarios(false));
        } else if (role !== 'doctor') {
            setDoctorHasHorarios(true);
            loadPremiumDoctors();
        }
    }, [role, doctorId]);

    if (checkingHorarios || (role === 'doctor' && doctorHasHorarios === null)) {
        return <Cargando />;
    }

    const modalConfiguraHorario = role === 'doctor' && doctorHasHorarios === false && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Configura tu horario primero</h2>
                <p className="text-gray-600 text-sm mb-6">
                    Para acceder al directorio de profesionales debes tener al menos un horario de disponibilidad programado.
                </p>
                <button
                    type="button"
                    onClick={() => navigate('/doctor/configuracion-citas', { state: { scrollToDisponibilidad: true } })}
                    className="w-full py-3 px-4 bg-[#1f94c2] hover:bg-[#1a7ba8] text-white font-semibold rounded-xl transition-colors"
                >
                    Ir a Horarios de disponibilidad
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/doctor/dashboard')}
                    className="mt-3 w-full py-2 px-4 text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                    Volver al escritorio
                </button>
            </div>
        </div>
    );

    if (role === 'doctor' && doctorHasHorarios === false) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-50 to-gray-100">
                {modalConfiguraHorario}
            </div>
        );
    }

    if (cargando) {
        return (
            <Cargando />
        );
    }

    const doctorsFiltrados = doctorService.filterDoctorsByService(doctors, servicio);
    const doctorsBySpecialty = !servicio ? doctorService.groupDoctorsBySpecialty(doctorsFiltrados) : [];
    const premiumDoctorsList = servicio
        ? doctorService.sortDoctorsForDisplay(doctorsFiltrados.filter(doc => doc.membershipPlan === 'premium'))
        : [];
    const freeDoctorsList = servicio
        ? doctorService.sortDoctorsForDisplay(doctorsFiltrados.filter(doc => doc.membershipPlan !== 'premium'))
        : [];

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 to-gray-100">
            {/* Header */}
            <header className="bg-gradient-to-br from-[#1f94c2] via-[#1a7ba8] to-[#205087] text-white shadow-xl">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur-sm shadow-inner">
                                <SvgIcon Icon={Directorio} className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold tracking-tight">
                                    Directorio de Profesionales
                                </h1>
                                <p className="text-white/90 text-xs mt-0.5">
                                    Encuentra el profesional de salud que necesitas
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Filtro sticky */}
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200/80 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                        Filtrar por especialidad
                    </label>
                    <Filtros setServicio={setServicio} />
                </div>
            </div>

            {/* Contenido */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
                {!servicio && doctorsBySpecialty.length > 0 && doctorsBySpecialty.map((group, index) => (
                    <section
                        key={group.key}
                        className={index > 0 ? 'mt-10 sm:mt-12 pt-8 sm:pt-10 border-t border-gray-200' : 'mb-2'}
                    >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#1f94c2] to-[#205087] text-white shadow-lg">
                                    <SvgIcon Icon={Directorio} className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#205087]">
                                        {group.label}
                                    </h2>
                                    <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                                        {group.doctors.length} profesional{group.doctors.length !== 1 ? 'es' : ''} disponible{group.doctors.length !== 1 ? 's' : ''}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <DoctorsGrid doctors={group.doctors} />
                    </section>
                ))}

                {servicio && premiumDoctorsList.length > 0 && (
                    <section className="mb-10 sm:mb-14">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 text-white shadow-lg shadow-amber-500/25">
                                    <FaCrown className="w-5 h-5 sm:w-6 sm:h-6" />
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#205087]">
                                        Profesionales Premium
                                    </h2>
                                    <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                                        Profesionales destacados
                                    </p>
                                </div>
                            </div>
                        </div>
                        <DoctorsGrid doctors={premiumDoctorsList} />
                    </section>
                )}

                {servicio && freeDoctorsList.length > 0 && (
                    <section className={premiumDoctorsList.length > 0 ? 'pt-8 sm:pt-10 border-t border-gray-200' : ''}>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 text-white shadow-lg">
                                    <SvgIcon Icon={Directorio} className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800">
                                        Otros Profesionales
                                    </h2>
                                    <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                                        Más profesionales disponibles
                                    </p>
                                </div>
                            </div>
                        </div>
                        <DoctorsGrid
                            doctors={freeDoctorsList}
                            gridClassName="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 lg:gap-6"
                        />
                    </section>
                )}

                {/* Estado vacío */}
                {doctorsFiltrados.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 sm:py-16 lg:py-20">
                        <div className="w-full max-w-md rounded-2xl bg-white p-6 sm:p-8 shadow-lg border border-gray-100 text-center">
                            <div className="mx-auto flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-gray-100 text-gray-400 mb-4">
                                <SvgIcon Icon={Directorio} className="w-8 h-8 sm:w-10 sm:h-10" />
                            </div>
                            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                                No hay profesionales disponibles
                            </h3>
                            <p className="text-gray-500 text-sm sm:text-base">
                                {servicio
                                    ? `No encontramos profesionales en "${doctorService.getSpecialtyLabel(servicio)}". Prueba otra especialidad.`
                                    : 'No hay profesionales registrados en este momento.'}
                            </p>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

/* Filtro por especialidad: solo las 10 especialidades actuales */
const Filtros = ({ setServicio }) => {
    return (
        <div className="relative w-full">
            <div className="flex items-center bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-[#1f94c2ff] transition-colors duration-200">
                <div className="flex items-center justify-center bg-[#1f94c2ff] px-4 py-3 rounded-l-lg">
                    <SvgIcon Icon={Buscar} className={"w-5 h-5 text-white"} />
                </div>
                <select
                    id="service"
                    name="service"
                    onChange={e => setServicio(e.target.value)}
                    className="flex-1 px-4 py-3 bg-white text-gray-700 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] cursor-pointer appearance-none pr-10 font-medium"
                >
                    <option value="">Todas las especialidades</option>
                    {ESPECIALIDADES_DIRECTORIO.map(esp => (
                        <option key={esp.value} value={esp.value}>{esp.label}</option>
                    ))}
                </select>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>
        </div>
    )
}

export default Marketplace;
